// ContactForm.tsx - With reCAPTCHA v3 and 30% larger fonts
import React, { useState } from "react";
import { SendHorizonal } from 'lucide-react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert
} from "reactstrap";
import LoadingOverlay from '../../Components/LoadingOverlay';

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  requestType: string;
  details: string;
  recaptchaToken: string;
}

interface ContactFormProps {
  onSubmitSuccess?: () => void;
  onSubmitError?: (error: string) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({
  onSubmitSuccess,
  onSubmitError
}) => {
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    requestType: "",
    details: "",
    recaptchaToken: ""
  });

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{
    type: 'success' | 'danger';
    message: string;
  } | null>(null);

  // Get reCAPTCHA v3 hook
  const { executeRecaptcha } = useGoogleReCaptcha();

  const requestTypes = [
    { value: "", label: "Select Request Type" },
    { value: "investor_interest", label: "Investor Interest" },
    { value: "technical_support_job", label: "Technical Support - Job" },
    { value: "technical_support_contract", label: "Technical Support - Contract" },
    { value: "general_question", label: "General Question" },
    { value: "partnership_inquiry", label: "Partnership Inquiry" },
    { value: "media_inquiry", label: "Media Inquiry" },
    { value: "bug_report", label: "Bug Report" },
    { value: "feature_request", label: "Feature Request" }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.firstName.trim()) return "First name is required";
    if (!formData.lastName.trim()) return "Last name is required";
    if (!formData.email.trim()) return "Email is required";
    if (!formData.requestType) return "Please select a request type";
    if (!formData.details.trim()) return "Please provide details";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return "Please enter a valid email address";
    }

    // Phone validation (if provided)
    if (formData.phone.trim()) {
      const phoneRegex = /^[\+]?[1-9]?[\d\s\-\(\)]{10,}$/;
      if (!phoneRegex.test(formData.phone.replace(/\s/g, ''))) {
        return "Please enter a valid phone number";
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setAlert({
        type: 'danger',
        message: validationError
      });
      return;
    }

    if (!executeRecaptcha) {
      setAlert({
        type: 'danger',
        message: 'reCAPTCHA not available. Please try again later.'
      });
      return;
    }

    setLoading(true);
    setAlert(null);

    try {
      // Execute reCAPTCHA v3
      const recaptchaToken = await executeRecaptcha('contact_form');

      if (!recaptchaToken) {
        throw new Error('Failed to generate reCAPTCHA token');
      }

      console.log('reCAPTCHA v3 token generated successfully');

      const submitData = {
        ...formData,
        recaptchaToken
      };

      const serverUrl = process.env.REACT_APP_SERVER;
      const apiUrl = `${serverUrl}/contact/submit`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setAlert({
          type: 'success',
          message: "Thank you for contacting us! We'll get back to you within 24 hours."
        });

        // Reset form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          requestType: "",
          details: "",
          recaptchaToken: ""
        });

        onSubmitSuccess?.();
      } else {
        throw new Error(result.error || 'Failed to submit contact form');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      setAlert({
        type: 'danger',
        message: errorMessage
      });
      onSubmitError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Custom style for larger fonts (30% increase)
  const largerFontStyle = {
    fontSize: '1.3rem', // 30% larger than base (1rem)
    lineHeight: '1.6'
  };

  const largerFontStyleForLabels = {
    fontSize: '1.15rem', // 30% larger than base
    fontWeight: '500'
  };

  const largerFontStyleForButton = {
    fontSize: '1.4rem', // 30% larger for button
    padding: '0.75rem 1.5rem'
  };

  return (
    <Card className="shadow-sm">
      <CardBody className="p-5">
        <h3 className="fs-3 fw-semibold mb-5 text-center" style={{ fontSize: '2.2rem' }}>Contact Us</h3>

        {alert && (
          <Alert
            color={alert.type}
            className="mb-4"
            toggle={() => setAlert(null)}
            style={largerFontStyle}
          >
            {alert.message}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <FormGroup className="mb-4">
                <Label for="firstName" className="form-label text-muted mb-2" style={largerFontStyleForLabels}>
                  First Name *
                </Label>
                <Input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                  style={largerFontStyle}
                  className="form-control-lg"
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup className="mb-4">
                <Label for="lastName" className="form-label text-muted mb-2" style={largerFontStyleForLabels}>
                  Last Name *
                </Label>
                <Input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                  style={largerFontStyle}
                  className="form-control-lg"
                />
              </FormGroup>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <FormGroup className="mb-4">
                <Label for="email" className="form-label text-muted mb-2" style={largerFontStyleForLabels}>
                  Email Address *
                </Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={loading}
                  required
                  style={largerFontStyle}
                  className="form-control-lg"
                />
              </FormGroup>
            </div>
            <div className="col-md-6">
              <FormGroup className="mb-4">
                <Label for="phone" className="form-label text-muted mb-2" style={largerFontStyleForLabels}>
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  name="phone"
                  id="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={loading}
                  placeholder="(555) 123-4567"
                  style={largerFontStyle}
                  className="form-control-lg"
                />
              </FormGroup>
            </div>
          </div>

          <FormGroup className="mb-4">
            <Label for="requestType" className="form-label text-muted mb-2" style={largerFontStyleForLabels}>
              Type of Request *
            </Label>
            <Input
              type="select"
              name="requestType"
              id="requestType"
              value={formData.requestType}
              onChange={handleInputChange}
              disabled={loading}
              required
              style={largerFontStyle}
              className="form-control-lg"
            >
              {requestTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup className="mb-4">
            <Label for="details" className="form-label text-muted mb-2" style={largerFontStyleForLabels}>
              Details *
            </Label>
            <Input
              type="textarea"
              name="details"
              id="details"
              value={formData.details}
              onChange={handleInputChange}
              disabled={loading}
              rows={6}
              placeholder="Please provide details about your request..."
              required
              style={largerFontStyle}
              className="form-control-lg"
            />
          </FormGroup>

              <LoadingOverlay
              isVisible={loading}
              size="medium"
              text="BidBro"
              subtext="Submitting info..."
              debug={false}
            />
          <Button
            type="submit"
            color="primary"
            size="lg"
            block
            disabled={loading}
            className="d-flex justify-content-center align-items-center mb-4"
            style={largerFontStyleForButton}
          >
            Submit Request <SendHorizonal className="ms-2" />
          </Button>
        </Form>

        <div className="text-center mt-4">
          <div className="text-muted" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
            We typically respond within 24 hours during business days.
            <br />
            
            <span className="text-success">
              🛡️ This form is protected by reCAPTCHA v3
            </span>
          </div>
        </div>

        {/* Debug info in development */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-3 p-3 bg-info-subtle border border-info rounded">
            <div className="text-info" style={{ fontSize: '1.1rem' }}>
              <strong>Development Mode:</strong> Using reCAPTCHA v3
              {executeRecaptcha ? ' ✅ Available' : ' ❌ Not Available'}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default ContactForm;